import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStatus, formatDateDot, truncateText } from '../../../utils'
import { useContractList, useUser } from '../../../hooks'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import styles from './HomeContractedSchedule.module.scss'

/**
 * 계약 유형 라벨 생성
 * 예: "국내 · 단일계약(2건)", "해외 · 기간계약(상반기)"
 */
const getContractTypeLabel = (contract) => {
	const parts = []
	// 지역
	if (contract.domesticContract && contract.overseasContract) parts.push('국내/해외')
	else if (contract.domesticContract) parts.push('국내')
	else if (contract.overseasContract) parts.push('해외')
	else if (contract.termContract) {
		parts.push(contract.termContract.isOverseasIncluded ? '해외포함' : '해외미포함')
	}
	// 계약 유형
	const tourCount = contract.contractTours?.length || 0
	switch (contract.contract_type) {
		case 'single':
			parts.push(`단일계약(${tourCount}건)`)
			break
		case 'long_term':
			parts.push(`장기계약(${tourCount}건)`)
			break
		case 'year_first_half':
			parts.push('기간계약(상반기)')
			break
		case 'year_second_half':
			parts.push('기간계약(하반기)')
			break
		case 'both':
			parts.push('기간계약(상반기/하반기)')
			break
		default:
			break
	}
	return parts.join(' · ') || '-'
}

/**
 * 홈 화면용 "계약된 일정" 섹션
 * - 체결/대기 중인 계약 목록 표시 (상대방 프로필, 대회, 기간, 유형, 상태)
 * - 없으면 빈 상태 + 프로찾기/캐디찾기 버튼
 */
export const HomeContractedSchedule = () => {
	const navigate = useNavigate()
	const [auth] = useAuthStatus()
	const { data: user } = useUser()
	const { data: contracts = [], isLoading } = useContractList()

	const activeContracts = useMemo(() => {
		if (!Array.isArray(contracts)) return []
		return contracts.filter(
			(c) =>
				c?.contract_status === 'PRE' ||
				c?.contract_status === 'PRO_ACCEPTED' ||
				c?.contract_status === 'ACCEPTED',
		)
	}, [contracts])

	const listItems = useMemo(() => {
		return activeContracts.map((contract) => {
			const userType = auth?.user?.userType
			const counterpart = userType === 'tourPro' ? contract.caddie : contract.player
			const counterpartName = counterpart?.name || '이름 없음'
			const counterpartImage = counterpart?.profile_image || '/images/pro-default.png'

			const tours = contract.contractTours || []
			// 대회가 여러 개일 수 있으므로 개별 대회명 배열로 전달
			const tournamentNames =
				tours.length === 0
					? []
					: tours.map((t) => truncateText(t.tour?.name || '대회명 없음', 28))

			let startDate = null
			let endDate = null
			if (
				contract.termContract != null &&
				contract.contract_started_at &&
				contract.contract_ended_at
			) {
				startDate = contract.contract_started_at
				endDate = contract.contract_ended_at
			} else if (tours.length > 0) {
				const starts = tours.map((t) => new Date(t.tour?.started_at).getTime())
				const ends = tours.map((t) => new Date(t.tour?.ended_at).getTime())
				startDate = starts.every(Number) ? new Date(Math.min(...starts)) : null
				endDate = ends.every(Number) ? new Date(Math.max(...ends)) : null
			}
			const periodLabel =
				startDate && endDate
					? `${formatDateDot(startDate)} ~ ${formatDateDot(endDate)}`
					: '-'

			const contractTypeLabel = getContractTypeLabel(contract)

			const statusLabel = contract.contract_status === 'ACCEPTED' ? '계약 체결' : '계약 대기'
			const statusType = contract.contract_status === 'ACCEPTED' ? 'complete' : 'wait'

			return {
				id: contract.id,
				counterpartName,
				counterpartImage,
				tournamentNames,
				periodLabel,
				contractTypeLabel,
				statusLabel,
				statusType,
			}
		})
	}, [activeContracts, auth?.user?.userType])

	const searchPath = '/search'
	const searchLabel = user?.userType === 'tourPro' ? '캐디 찾기' : '프로 찾기'

	if (isLoading) {
		return (
			<div className={styles.section}>
				<div className={styles.titleBox}>
					<span className={styles.boxLabel}>계약 현황</span>
				</div>
				<div className={styles.emptyState}>
					<span className={styles.emptyText}>불러오는 중...</span>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.section}>
			<div className={styles.titleBox}>
				<span className={styles.boxLabel}>계약 현황</span>
			</div>
			{listItems.length === 0 ? (
				<div className={styles.emptyState}>
					<div className={styles.emptyTitle}>아직 계약된 일정이 없네요.</div>
					<button
						type="button"
						className={styles.searchButton}
						onClick={() => navigate(searchPath)}>
						{searchLabel}
					</button>
				</div>
			) : (
				<ul className={styles.list}>
					{listItems.map((item) => (
						<li key={item.id}>
							<div className={styles.card}>
								{/* 상단: 프로필 + 이름 + 상태 */}
								<div className={styles.cardHeader}>
									<div className={styles.cardHeaderLeft}>
										<div className={styles.profileImageArea}>
											<img
												src={item.counterpartImage}
												alt={`${item.counterpartName} 프로필`}
												onError={(e) => {
													e.target.src = '/images/pro-default.png'
												}}
											/>
										</div>
										<div className={styles.headerInfo}>
											<span className={styles.counterpartName}>
												{item.counterpartName}
											</span>
											<span
												className={`${styles.statusBadge} ${styles[item.statusType]}`}>
												{item.statusLabel}
											</span>
										</div>
									</div>
								</div>
								{/* 중간: 계약 정보 */}
								<div className={styles.cardBody}>
									<div className={styles.infoRow}>
										<span className={styles.infoLabel}>대회</span>
										{item.tournamentNames.length === 0 ? (
											<span className={styles.infoValue}>대회미정</span>
										) : item.tournamentNames.length === 1 ? (
											<span className={styles.infoValue}>
												{item.tournamentNames[0]}
											</span>
										) : (
											<div className={styles.tournamentList}>
												{item.tournamentNames.map((name, idx) => (
													<span
														key={idx}
														className={styles.tournamentItem}>
														{name}
													</span>
												))}
											</div>
										)}
									</div>
									<div className={styles.infoRow}>
										<span className={styles.infoLabel}>기간</span>
										<span className={styles.infoValue}>{item.periodLabel}</span>
									</div>
									<div className={styles.infoRow}>
										<span className={styles.infoLabel}>유형</span>
										<span className={styles.infoValue}>
											{item.contractTypeLabel}
										</span>
									</div>
								</div>
								{/* 하단: 상세 보기 버튼 */}
								<button
									type="button"
									className={styles.detailButton}
									onClick={() => navigate(`/contracts/${item.id}`)}>
									상세 계약조건 보기
									<ChevronRightIcon className={styles.detailButtonIcon} />
								</button>
							</div>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}
